import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';
import { isTicketPath } from '../../utils/publicAccessPaths';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const CACHE_MS = 5000;
const POLL_INTERVAL = 10000;

const SKIP_PATHS = ['/showtime', '/showtime/lobby', '/showtime/payment', '/login', '/signup', '/auth/callback', '/verify-email', '/reset-password', '/adminlogin', '/sp-login'];

const shouldSkip = (pathname) => {
  const path = pathname.toLowerCase();
  if (isTicketPath(pathname)) return true;
  return SKIP_PATHS.some((p) => path.startsWith(p)) || path.startsWith('/admin');
};

const ShowTimeSessionTracker = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const lastCheckRef = useRef(0);
  const lastResultRef = useRef(null);
  const sessionCheckInFlightRef = useRef(false);
  const wasActiveRef = useRef(null);
  const loggedOutRef = useRef(false);

  const isAdminPath = location.pathname.toLowerCase().startsWith('/admin');

  // Poll /is-active to detect show time start → logout + toast (user-side only)
  useEffect(() => {
    if (isAdminPath) return;
    let cancelled = false;

    const checkActive = () => {
      fetch(`${API_BASE}/api/show-times/is-active`)
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          const nowActive = !!data.active;
          if (wasActiveRef.current === false && nowActive && !loggedOutRef.current) {
            loggedOutRef.current = true;
            enqueueSnackbar("It's show time! Please log in to join.", { variant: 'info', autoHideDuration: 6000 });
            if (isAuthenticated) {
              logout();
              navigate('/login', { replace: true });
            }
          }
          if (!nowActive) {
            loggedOutRef.current = false;
          }
          wasActiveRef.current = nowActive;
        })
        .catch(() => {});
    };

    checkActive();
    const interval = setInterval(checkActive, POLL_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAdminPath, isAuthenticated, logout, navigate, enqueueSnackbar]);

  // Session check for logged-in users
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (shouldSkip(location.pathname)) return;
    if (sessionCheckInFlightRef.current) return;

    const now = Date.now();
    if (now - lastCheckRef.current < CACHE_MS && lastResultRef.current != null) {
      if (lastResultRef.current.redirectTo) {
        navigate(lastResultRef.current.redirectTo, { replace: true });
      }
      return;
    }

    let cancelled = false;
    sessionCheckInFlightRef.current = true;
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    fetch(`${API_BASE}/api/show-times/session-check`, { headers })
      .then(async (res) => {
        let data = { hasActive: false };
        try {
          const parsed = await res.json();
          if (res.ok && parsed && typeof parsed === 'object') data = parsed;
        } catch {
          /* ignore */
        }
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        lastCheckRef.current = Date.now();
        lastResultRef.current = data;
        if (data.redirectTo) {
          navigate(data.redirectTo, { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          lastCheckRef.current = Date.now();
          lastResultRef.current = { hasActive: false };
        }
      })
      .finally(() => {
        sessionCheckInFlightRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, location.pathname, navigate]);

  return null;
};

export default ShowTimeSessionTracker;
